import { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { useConfirm } from "../../components/ConfirmDialog";
import { Package, Plus, Pencil, Trash2, X, Save, Loader2, Tags, Settings2, Layers } from "lucide-react";
import { toast } from "sonner";

/**
 * Car Extras — dedicated admin page for the three catalogues that power
 * every vehicle listing + booking form:
 *   • Car Features (free specs — Air-Con, Automatic, …)
 *   • Paid Extras (paid add-ons — GPS, Child Seat, …)
 *   • Car Categories (Economy, Compact, SUV, Luxury, …) — v3.9.5
 *
 * v3.9.5: Introduced tabbed navigation between the three catalogues so
 * they no longer stack vertically. Added Categories tab that drives the
 * Fleet editor category dropdown dynamically.
 */
const TABS = [
  { key: "features",   label: "Features",     icon: <Settings2 size={14} /> },
  { key: "extras",     label: "Paid Extras",  icon: <Tags size={14} /> },
  { key: "categories", label: "Categories",   icon: <Layers size={14} /> },
];

export default function CarExtras() {
  const [tab, setTab] = useState("features");
  return (
    <div className="max-w-6xl px-6 lg:px-10 pt-6 lg:pt-10 pb-12" data-testid="car-extras-page">
      <div className="mb-8">
        <p className="text-[10px] tracking-[0.3em] uppercase text-neutral-500 dark:text-neutral-400">
          Catalogue
        </p>
        <h1 className="font-display text-4xl md:text-5xl tracking-tight mt-2 flex items-center gap-3">
          <Package size={28} /> Car Extras
        </h1>
        <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-2 max-w-2xl">
          Manage the free <em>Car Features</em>, paid <em>Extras</em> and vehicle
          <em> Categories</em> that power the Fleet editor and every booking form.
        </p>
      </div>

      <div
        className="flex items-center gap-1 border-b border-black/8 dark:border-white/10 mb-6"
        role="tablist"
        data-testid="car-extras-tabs"
      >
        {TABS.map((t) => {
          const active = tab === t.key;
          return (
            <button
              key={t.key}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => setTab(t.key)}
              data-testid={`car-extras-tab-${t.key}`}
              className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm border-b-2 -mb-px transition-colors ${
                active
                  ? "border-black dark:border-white text-black dark:text-white font-medium"
                  : "border-transparent text-neutral-500 dark:text-neutral-400 hover:text-black dark:hover:text-white"
              }`}
            >
              {t.icon} {t.label}
            </button>
          );
        })}
      </div>

      {tab === "features"   && <FeaturesSection />}
      {tab === "extras"     && <ExtrasSection />}
      {tab === "categories" && <CategoriesSection />}
    </div>
  );
}

// ── Features ────────────────────────────────────────────────────────────────
function FeaturesSection() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const { confirm, dialog: confirmDialog } = useConfirm();

  const load = useCallback(() => {
    setLoading(true);
    api.get("/admin/features")
      .then((r) => setItems(Array.isArray(r.data) ? r.data : []))
      .catch((e) => toast.error(formatApiError(e?.response?.data?.detail) || e.message))
      .finally(() => setLoading(false));
  }, []);
  useEffect(load, [load]);

  const save = async (form) => {
    try {
      if (form.id) {
        await api.put(`/admin/features/${form.id}`, { name: form.name, status: form.status });
        toast.success("Feature updated");
      } else {
        await api.post("/admin/features", { name: form.name, status: form.status });
        toast.success("Feature created");
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || e.message);
    }
  };

  const remove = async (item) => {
    const ok = await confirm({
      title:        "Delete feature",
      message:      "It will be removed from every vehicle that references it.",
      subject:      item.name,
      confirmLabel: "Delete feature",
      testId:       "confirm-delete-feature",
    });
    if (!ok) return;
    setBusyId(item.id);
    try {
      await api.delete(`/admin/features/${item.id}`);
      toast.success("Feature deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || e.message);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <section
      className="bg-white dark:bg-[#1F2937] border border-black/10 dark:border-white/10 rounded-lg overflow-hidden"
      data-testid="features-section"
    >
      <header className="px-6 py-4 flex items-center justify-between border-b border-black/8 dark:border-white/10 bg-[#F3F4F6] dark:bg-[#111827]">
        <div>
          <p className="text-[10px] tracking-[0.3em] uppercase text-neutral-500 dark:text-neutral-400">Free</p>
          <h2 className="font-display text-xl mt-1">Car Features</h2>
          <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">
            Zero-price vehicle specs (A/C, Automatic, Bluetooth…). Attach them to a Car Group in the vehicle editor.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setEditing({ mode: "create", form: { name: "", status: "active" } })}
          className="bg-black text-white dark:bg-white dark:text-black px-3 py-1.5 text-xs rounded inline-flex items-center gap-1 hover:opacity-90"
          data-testid="add-feature-btn"
        >
          <Plus size={12} /> New feature
        </button>
      </header>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-[10px] uppercase tracking-wider text-neutral-500 dark:text-neutral-400 border-b border-black/8 dark:border-white/10">
            <tr>
              <th className="py-2 px-6">Name</th>
              <th className="py-2 px-4">Status</th>
              <th className="py-2 px-4 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={3} className="py-6 text-center text-neutral-500 dark:text-neutral-400">Loading…</td></tr>
            )}
            {!loading && items.length === 0 && (
              <tr><td colSpan={3} className="py-6 text-center text-neutral-500 dark:text-neutral-400" data-testid="features-empty">
                No features yet. Click <em>New feature</em> to add one.
              </td></tr>
            )}
            {items.map((f) => (
              <tr key={f.id} className="border-b border-black/5 dark:border-white/5" data-testid={`feature-row-${f.id}`}>
                <td className="py-3 px-6">{f.name}</td>
                <td className="py-3 px-4"><StatusPill status={f.status} /></td>
                <td className="py-3 px-4">
                  <div className="flex justify-end gap-1">
                    <IconBtn onClick={() => setEditing({ mode: "edit", form: { ...f } })} testId={`edit-feature-${f.id}`} label="Edit"><Pencil size={14} /></IconBtn>
                    <IconBtn onClick={() => remove(f)} testId={`delete-feature-${f.id}`} label="Delete" danger disabled={busyId === f.id}>
                      {busyId === f.id ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                    </IconBtn>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <FeatureModal
          initial={editing.form}
          mode={editing.mode}
          onCancel={() => setEditing(null)}
          onSave={save}
        />
      )}
      {confirmDialog}
    </section>
  );
}

// ── Extras ──────────────────────────────────────────────────────────────────
function ExtrasSection() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const { confirm, dialog: confirmDialog } = useConfirm();

  const load = useCallback(() => {
    setLoading(true);
    api.get("/admin/extras")
      .then((r) => setItems(Array.isArray(r.data) ? r.data : []))
      .catch((e) => toast.error(formatApiError(e?.response?.data?.detail) || e.message))
      .finally(() => setLoading(false));
  }, []);
  useEffect(load, [load]);

  const save = async (form) => {
    try {
      const payload = {
        name: form.name,
        price: Number(form.price),
        pricing_type: form.pricing_type,
        status: form.status,
      };
      if (form.id) {
        await api.put(`/admin/extras/${form.id}`, payload);
        toast.success("Extra updated");
      } else {
        await api.post("/admin/extras", payload);
        toast.success("Extra created");
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || e.message);
    }
  };

  const remove = async (item) => {
    const ok = await confirm({
      title:        "Delete extra",
      message:      "Existing bookings keep it as a historical reference; it just disappears from future bookings.",
      subject:      item.name,
      confirmLabel: "Delete extra",
      testId:       "confirm-delete-extra",
    });
    if (!ok) return;
    setBusyId(item.id);
    try {
      await api.delete(`/admin/extras/${item.id}`);
      toast.success("Extra deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || e.message);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <section
      className="bg-white dark:bg-[#1F2937] border border-black/10 dark:border-white/10 rounded-lg overflow-hidden"
      data-testid="extras-section"
    >
      <header className="px-6 py-4 flex items-center justify-between border-b border-black/8 dark:border-white/10 bg-[#F3F4F6] dark:bg-[#111827]">
        <div>
          <p className="text-[10px] tracking-[0.3em] uppercase text-neutral-500 dark:text-neutral-400">Paid</p>
          <h2 className="font-display text-xl mt-1">Extras</h2>
          <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">
            Paid add-ons priced <strong>per day</strong> or <strong>per reservation</strong>. Active extras appear in every booking form.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setEditing({ mode: "create", form: { name: "", price: 0, pricing_type: "per_day", status: "active" } })}
          className="bg-black text-white dark:bg-white dark:text-black px-3 py-1.5 text-xs rounded inline-flex items-center gap-1 hover:opacity-90"
          data-testid="add-extra-btn"
        >
          <Plus size={12} /> New extra
        </button>
      </header>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-[10px] uppercase tracking-wider text-neutral-500 dark:text-neutral-400 border-b border-black/8 dark:border-white/10">
            <tr>
              <th className="py-2 px-6">Name</th>
              <th className="py-2 px-4">Price</th>
              <th className="py-2 px-4">Pricing</th>
              <th className="py-2 px-4">Status</th>
              <th className="py-2 px-4 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={5} className="py-6 text-center text-neutral-500 dark:text-neutral-400">Loading…</td></tr>
            )}
            {!loading && items.length === 0 && (
              <tr><td colSpan={5} className="py-6 text-center text-neutral-500 dark:text-neutral-400" data-testid="extras-empty">
                No extras yet.
              </td></tr>
            )}
            {items.map((x) => (
              <tr key={x.id} className="border-b border-black/5 dark:border-white/5" data-testid={`extra-row-${x.id}`}>
                <td className="py-3 px-6">{x.name}</td>
                <td className="py-3 px-4 tabular-nums">€{Number(x.price).toFixed(2)}</td>
                <td className="py-3 px-4">
                  <span className="text-[11px] px-2 py-0.5 rounded bg-black/5 dark:bg-white/10">
                    {x.pricing_type === "per_day" ? "per day" : "per reservation"}
                  </span>
                </td>
                <td className="py-3 px-4"><StatusPill status={x.status} /></td>
                <td className="py-3 px-4">
                  <div className="flex justify-end gap-1">
                    <IconBtn onClick={() => setEditing({ mode: "edit", form: { ...x } })} testId={`edit-extra-${x.id}`} label="Edit"><Pencil size={14} /></IconBtn>
                    <IconBtn onClick={() => remove(x)} testId={`delete-extra-${x.id}`} label="Delete" danger disabled={busyId === x.id}>
                      {busyId === x.id ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                    </IconBtn>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <ExtraModal
          initial={editing.form}
          mode={editing.mode}
          onCancel={() => setEditing(null)}
          onSave={save}
        />
      )}
      {confirmDialog}
    </section>
  );
}

// ── Modals & atoms ──────────────────────────────────────────────────────────
function FeatureModal({ initial, mode, onCancel, onSave }) {
  const [form, setForm] = useState(initial);
  return (
    <ModalShell title={mode === "edit" ? "Edit feature" : "New feature"} onCancel={onCancel}
      onSubmit={(e) => { e.preventDefault(); onSave(form); }}>
      <FieldLabel label="Name">
        <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
          data-testid="feature-name-input"
          className="w-full bg-transparent border-b border-black/15 dark:border-white/15 py-1.5 outline-none focus:border-black dark:focus:border-white text-sm" />
      </FieldLabel>
      <FieldLabel label="Status">
        <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}
          data-testid="feature-status-input"
          className="w-full bg-transparent border-b border-black/15 dark:border-white/15 py-1.5 outline-none text-sm">
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
      </FieldLabel>
    </ModalShell>
  );
}

function ExtraModal({ initial, mode, onCancel, onSave }) {
  const [form, setForm] = useState(initial);
  return (
    <ModalShell title={mode === "edit" ? "Edit extra" : "New extra"} onCancel={onCancel}
      onSubmit={(e) => { e.preventDefault(); onSave(form); }}>
      <FieldLabel label="Name">
        <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
          data-testid="extra-name-input"
          className="w-full bg-transparent border-b border-black/15 dark:border-white/15 py-1.5 outline-none focus:border-black dark:focus:border-white text-sm" />
      </FieldLabel>
      <div className="grid grid-cols-2 gap-6">
        <FieldLabel label="Price (€)">
          <input required type="number" min="0" step="0.01"
            value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })}
            data-testid="extra-price-input"
            className="w-full bg-transparent border-b border-black/15 dark:border-white/15 py-1.5 outline-none focus:border-black dark:focus:border-white text-sm tabular-nums" />
        </FieldLabel>
        <FieldLabel label="Pricing type">
          <select value={form.pricing_type} onChange={(e) => setForm({ ...form, pricing_type: e.target.value })}
            data-testid="extra-pricing-type-input"
            className="w-full bg-transparent border-b border-black/15 dark:border-white/15 py-1.5 outline-none text-sm">
            <option value="per_day">Per day</option>
            <option value="per_reservation">Per reservation</option>
          </select>
        </FieldLabel>
      </div>
      <FieldLabel label="Status">
        <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}
          data-testid="extra-status-input"
          className="w-full bg-transparent border-b border-black/15 dark:border-white/15 py-1.5 outline-none text-sm">
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
      </FieldLabel>
    </ModalShell>
  );
}

function ModalShell({ title, children, onCancel, onSubmit }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onCancel}
      data-testid="car-extras-modal">
      <div className="bg-white dark:bg-[#1F2937] max-w-md w-full p-6 rounded border border-black/10 dark:border-white/10" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-xl">{title}</h3>
          <button onClick={onCancel} className="text-neutral-400 hover:text-black dark:hover:text-white" aria-label="Close"
            data-testid="modal-close-btn"><X size={18} /></button>
        </div>
        <form onSubmit={onSubmit} className="space-y-4">
          {children}
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onCancel}
              className="border border-black/15 dark:border-white/15 px-4 py-1.5 text-sm rounded hover:border-black dark:hover:border-white"
              data-testid="modal-cancel-btn">
              Cancel
            </button>
            <button type="submit"
              className="bg-black text-white dark:bg-white dark:text-black px-4 py-1.5 text-sm rounded inline-flex items-center gap-1 hover:opacity-90"
              data-testid="modal-save-btn">
              <Save size={12} /> Save
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function FieldLabel({ label, children }) {
  return (
    <label className="block">
      <span className="text-[10px] tracking-[0.25em] uppercase text-neutral-500 dark:text-neutral-400 block mb-1">{label}</span>
      {children}
    </label>
  );
}

function StatusPill({ status }) {
  const active = status === "active";
  return (
    <span
      className={`text-[10px] px-2 py-0.5 rounded ${active
        ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-950/50 dark:text-emerald-300"
        : "bg-neutral-200 text-neutral-700 dark:bg-neutral-800 dark:text-neutral-300"}`}
    >
      {active ? "Active" : "Inactive"}
    </span>
  );
}

function IconBtn({ children, onClick, testId, danger, label, disabled }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-testid={testId}
      aria-label={label}
      title={label}
      className={`p-1.5 rounded border border-transparent hover:border-black/15 dark:hover:border-white/15 transition-colors disabled:opacity-40 ${
        danger ? "text-rose-600 dark:text-rose-400" : "text-neutral-600 dark:text-neutral-300"
      }`}
    >
      {children}
    </button>
  );
}

// ── Categories (v3.9.5) ─────────────────────────────────────────────────────
function CategoriesSection() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const { confirm, dialog: confirmDialog } = useConfirm();

  const load = useCallback(() => {
    setLoading(true);
    api.get("/admin/car-categories")
      .then((r) => setItems(Array.isArray(r.data) ? r.data : []))
      .catch((e) => toast.error(formatApiError(e?.response?.data?.detail) || e.message))
      .finally(() => setLoading(false));
  }, []);
  useEffect(load, [load]);

  const save = async (form) => {
    try {
      const payload = {
        name:       form.name,
        status:     form.status,
        sort_order: Number(form.sort_order) || 0,
      };
      if (form.id) {
        await api.put(`/admin/car-categories/${form.id}`, payload);
        toast.success("Category updated");
      } else {
        await api.post("/admin/car-categories", payload);
        toast.success("Category created");
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || e.message);
    }
  };

  const remove = async (item) => {
    const ok = await confirm({
      title:        "Delete category",
      message:      "Existing vehicles keep the category label; it just disappears from the Fleet editor dropdown.",
      subject:      item.name,
      confirmLabel: "Delete category",
      testId:       "confirm-delete-category",
    });
    if (!ok) return;
    setBusyId(item.id);
    try {
      await api.delete(`/admin/car-categories/${item.id}`);
      toast.success("Category deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || e.message);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <section
      className="bg-white dark:bg-[#1F2937] border border-black/10 dark:border-white/10 rounded-lg overflow-hidden"
      data-testid="categories-section"
    >
      <header className="px-6 py-4 flex items-center justify-between border-b border-black/8 dark:border-white/10 bg-[#F3F4F6] dark:bg-[#111827]">
        <div>
          <p className="text-[10px] tracking-[0.3em] uppercase text-neutral-500 dark:text-neutral-400">Segments</p>
          <h2 className="font-display text-xl mt-1">Car Categories</h2>
          <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">
            Vehicle segments (Economy, Compact, SUV, Convertible, Luxury…). Active
            categories power the Fleet editor <em>Category</em> dropdown.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setEditing({ mode: "create", form: { name: "", status: "active", sort_order: 0 } })}
          className="bg-black text-white dark:bg-white dark:text-black px-3 py-1.5 text-xs rounded inline-flex items-center gap-1 hover:opacity-90"
          data-testid="add-category-btn"
        >
          <Plus size={12} /> New category
        </button>
      </header>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-[10px] uppercase tracking-wider text-neutral-500 dark:text-neutral-400 border-b border-black/8 dark:border-white/10">
            <tr>
              <th className="py-2 px-6">Name</th>
              <th className="py-2 px-4">Sort order</th>
              <th className="py-2 px-4">Status</th>
              <th className="py-2 px-4 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={4} className="py-6 text-center text-neutral-500 dark:text-neutral-400">Loading…</td></tr>
            )}
            {!loading && items.length === 0 && (
              <tr><td colSpan={4} className="py-6 text-center text-neutral-500 dark:text-neutral-400" data-testid="categories-empty">
                No categories yet. Click <em>New category</em> to add one.
              </td></tr>
            )}
            {items.map((c) => (
              <tr key={c.id} className="border-b border-black/5 dark:border-white/5" data-testid={`category-row-${c.id}`}>
                <td className="py-3 px-6 capitalize">{c.name}</td>
                <td className="py-3 px-4 tabular-nums text-neutral-500">{c.sort_order ?? 0}</td>
                <td className="py-3 px-4"><StatusPill status={c.status} /></td>
                <td className="py-3 px-4">
                  <div className="flex justify-end gap-1">
                    <IconBtn onClick={() => setEditing({ mode: "edit", form: { ...c } })} testId={`edit-category-${c.id}`} label="Edit"><Pencil size={14} /></IconBtn>
                    <IconBtn onClick={() => remove(c)} testId={`delete-category-${c.id}`} label="Delete" danger disabled={busyId === c.id}>
                      {busyId === c.id ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                    </IconBtn>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <CategoryModal
          initial={editing.form}
          mode={editing.mode}
          onCancel={() => setEditing(null)}
          onSave={save}
        />
      )}
      {confirmDialog}
    </section>
  );
}

function CategoryModal({ initial, mode, onCancel, onSave }) {
  const [form, setForm] = useState(initial);
  const submit = (e) => { e.preventDefault(); onSave(form); };
  return (
    <ModalShell
      title={mode === "create" ? "New category" : "Edit category"}
      onCancel={onCancel}
      onSubmit={submit}
    >
      <FieldLabel label="Name">
        <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
          placeholder="e.g. Compact, SUV, Convertible"
          data-testid="category-name-input"
          className="w-full bg-transparent border-b border-black/15 dark:border-white/15 py-1.5 outline-none focus:border-black dark:focus:border-white text-sm" />
      </FieldLabel>
      <div className="grid grid-cols-2 gap-6">
        <FieldLabel label="Sort order">
          <input type="number" min="0" step="1"
            value={form.sort_order ?? 0}
            onChange={(e) => setForm({ ...form, sort_order: e.target.value })}
            data-testid="category-sort-input"
            className="w-full bg-transparent border-b border-black/15 dark:border-white/15 py-1.5 outline-none focus:border-black dark:focus:border-white text-sm tabular-nums" />
        </FieldLabel>
        <FieldLabel label="Status">
          <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}
            data-testid="category-status-input"
            className="w-full bg-transparent border-b border-black/15 dark:border-white/15 py-1.5 outline-none text-sm">
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </FieldLabel>
      </div>
    </ModalShell>
  );
}
